import { Head, Link, usePage } from "@inertiajs/react"
import { Activity, AlertTriangle, CheckCircle2, Clock3, FileText, Send } from "lucide-react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card"
import { AppLayout } from "../components/layout/AppLayout"
import { buttonVariants } from "../components/ui/button"
import { cn } from "../lib/cn"
import { EmissaoStatusBadge } from "../features/invoices/emissaoStatus"
import type { SharedPageProps } from "../types/shared"

type DashboardProps = SharedPageProps & {
  metrics: {
    emitidas_hoje_count: number
    emitidas_hoje_valor: number
    prontas_count: number
    prontas_valor: number
    em_emissao_count: number
    erro_count: number
    emitidas_mes_count: number
    emitidas_mes_valor: number
  }
  recent_invoices: Array<{
    id: number
    company_name: string
    client_name: string
    valor: number
    emissao_status: string
    updated_at?: string
  }>
}

function money(value: number) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value || 0)
}

export default function Dashboard() {
  const { props } = usePage<DashboardProps>()
  const userEmail = props.auth.user?.email ?? "Usuário"
  const m = props.metrics

  return (
    <>
      <Head title="Dashboard" />
      <AppLayout>
        <div className="mx-auto flex w-full max-w-7xl flex-col p-6">
          <header className="mb-8">
            <h1 className="text-3xl font-bold text-white">Dashboard</h1>
            <p className="text-sm text-slate-400">Visão operacional da emissão de notas.</p>
          </header>

          <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            <Card className="border-slate-800 bg-slate-900">
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2 text-slate-400">
                  <CheckCircle2 className="h-4 w-4 text-emerald-400" /> Emitidas hoje
                </CardDescription>
                <CardTitle className="text-2xl text-white">{m.emitidas_hoje_count}</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-slate-300">{money(m.emitidas_hoje_valor)}</CardContent>
            </Card>

            <Card className="border-slate-800 bg-slate-900">
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2 text-slate-400">
                  <Send className="h-4 w-4 text-indigo-400" /> Prontas para enviar
                </CardDescription>
                <CardTitle className="text-2xl text-white">{m.prontas_count}</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-slate-300">{money(m.prontas_valor)}</CardContent>
            </Card>

            <Card className="border-slate-800 bg-slate-900">
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2 text-slate-400">
                  <Clock3 className="h-4 w-4 text-sky-400" /> Em emissão
                </CardDescription>
                <CardTitle className="text-2xl text-white">{m.em_emissao_count}</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-slate-300">Aguardando retorno/webhook</CardContent>
            </Card>

            <Card className="border-slate-800 bg-slate-900">
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2 text-slate-400">
                  <AlertTriangle className="h-4 w-4 text-rose-400" /> Com erro
                </CardDescription>
                <CardTitle className="text-2xl text-white">{m.erro_count}</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-slate-300">Requer revisão/reenvio</CardContent>
            </Card>
          </div>

          <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
            <Card className="border-slate-800 bg-slate-900 lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-white">Últimas notas</CardTitle>
                <CardDescription className="text-slate-400">Acompanhe rapidamente os últimos status.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {props.recent_invoices.map((inv) => (
                    <div key={inv.id} className="flex items-center justify-between rounded-md border border-slate-800 bg-slate-950/40 px-3 py-2">
                      <div>
                        <p className="text-sm text-slate-100">#{inv.id} · {inv.client_name || "Cliente"}</p>
                        <p className="text-xs text-slate-500">{inv.company_name || "—"} · {money(inv.valor)}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <EmissaoStatusBadge status={inv.emissao_status} />
                        <Link href={`/notas-fiscais/${inv.id}`} className={cn(buttonVariants({ variant: "outline", size: "sm" }), "border-slate-700 bg-transparent")}>
                          Ver
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-800 bg-slate-900">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <Activity className="h-4 w-4 text-emerald-400" />
                  Resumo do mês
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-xs text-slate-400">Notas emitidas no mês</p>
                  <p className="text-xl font-semibold text-white">{m.emitidas_mes_count}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Valor emitido no mês</p>
                  <p className="text-xl font-semibold text-emerald-300">{money(m.emitidas_mes_valor)}</p>
                </div>
                <div className="rounded-md border border-slate-800 bg-slate-950/40 p-3 text-xs text-slate-400">
                  Sessão atual: <span className="text-slate-200">{userEmail}</span>
                </div>
                <Link href="/notas-fiscais" className={cn(buttonVariants(), "w-full")}>
                  <FileText className="mr-2 h-4 w-4" />
                  Abrir notas fiscais
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </AppLayout>
    </>
  )
}
